import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isStaffRole } from "@lms/backend/lib/auth/post-login-redirect";
import { CourseGradebook } from "@/components/instructor/course-gradebook";
import { CourseSwitcher } from "@/components/instructor/course-switcher";
import { PageShell } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";
import { fetchCourseBuilder } from "@/services/api/instructor";

export function TenantInstructorCourseGradebookPage() {
  const { tenantSlug = "", courseId = "" } = useParams<{ tenantSlug: string; courseId: string }>();
  const t = useTranslations("instructor");
  const tTools = useTranslations("instructorCourseTools");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const courseQuery = useQuery({
    queryKey: ["instructor-course-title", courseId],
    queryFn: () => fetchCourseBuilder(courseId),
    enabled: isAuthenticated && isStaffRole(user?.role) && Boolean(courseId),
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (!isStaffRole(user?.role)) {
    return <Navigate to={`/t/${tenantSlug}/dashboard`} replace />;
  }

  if (courseQuery.isLoading) {
    return (
      <PageShell width="wide">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </PageShell>
    );
  }

  const courseTitle = courseQuery.data?.course.title ?? tTools("courseTitleFallback");

  return (
    // Wide: this page is mostly tables, which the default measure starves; each table
    // keeps its own overflow-x container for narrow screens.
    <PageShell width="wide">
      <CourseSwitcher tenantSlug={tenantSlug} courseId={courseId} section="gradebook" />
      <CourseGradebook tenantSlug={tenantSlug} courseId={courseId} courseTitle={courseTitle} />
    </PageShell>
  );
}
